import { scannerMessages } from "constants/scanner";

import { type DriverActionResult } from "./adapters/driverEffects";
import { Xrs2iDriverSession } from "./runtime/Xrs2iDriverSession";

/**
 * Public entrypoint for the TruTest XRS2i driver.
 * It exposes the driver name, supported scanner messages, and the session map that keeps
 * one protocol session alive per device ID so repeated commands share the same state.
 */
export const TT_XRS2I_DRIVER_NAME = "tt-xrs2i";
export const TT_XRS2I_CAPABILITIES = [
  scannerMessages.CANCEL_READ_SAVED_SCANS,
  scannerMessages.READ_SAVED_SCANS,
];

interface DeviceLike {
  deviceId?: string;
  id?: string;
  name?: string;
}

export interface DriverLifecycleHooks {
  onConnected?: (deviceId: string) => DriverActionResult;
  onDisconnected?: (deviceId: string) => void;
  onTick?: (deviceId: string) => DriverActionResult;
  tickIntervalMs?: number;
}

const sessionByDeviceId = new Map<string, Xrs2iDriverSession>();

function getDeviceId(device: DeviceLike): string | undefined {
  return device.deviceId ?? device.id;
}

function getOrCreateSession(deviceId: string): Xrs2iDriverSession {
  const existingSession = sessionByDeviceId.get(deviceId);
  if (existingSession) {
    return existingSession;
  }

  const nextSession = new Xrs2iDriverSession(deviceId);
  sessionByDeviceId.set(deviceId, nextSession);
  return nextSession;
}

/**
 * Starts the saved-scan download flow for a device.
 * The session object keeps command sequencing, retries, and progress reporting isolated from callers.
 */
export function truTestXRS2IReadSavedScans(
  deviceId: string,
): DriverActionResult {
  return getOrCreateSession(deviceId).readSavedScans();
}

/**
 * Synchronises the scanner hardware state before normal interaction resumes.
 * This is used when the driver needs the device back in a known protocol mode.
 */
export function truTestXRS2ISyncState(deviceId: string): DriverActionResult {
  return getOrCreateSession(deviceId).syncState();
}

/**
 * Cancels an active saved-scan download for the given device.
 * The session is left alive long enough to ignore late frames from the cancelled request.
 */
export function truTestXRS2ICancelReadSavedScans(
  deviceId: string,
): DriverActionResult {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }

  return session.cancelReadSavedScans();
}

/**
 * Destroys the driver session for a device and removes its cached protocol state.
 * This prevents stale retries or queued commands from leaking into a future connection.
 */
export function truTestXRS2IDestroySession(deviceId: string): void {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }

  session.destroy();
  sessionByDeviceId.delete(deviceId);
}

/**
 * Flushes any pending timer-driven work for the device session.
 * The caller uses this when delayed retries or completion work need to be turned into Redux actions.
 */
function truTestXRS2ITick(deviceId: string): DriverActionResult {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }

  return session.tick();
}

/**
 * Routes raw scanner payloads into the device session for the matching device ID.
 * This keeps frame parsing, download state, and scan conversion inside the protocol layer.
 */
export const truTestXRS2IProcessMessage = (
  device: DeviceLike,
  _type: string,
  _status: unknown,
  serializedPayload: string,
): DriverActionResult => {
  const deviceId = getDeviceId(device);
  if (!deviceId) {
    return;
  }

  return getOrCreateSession(deviceId).receive(serializedPayload);
};

/**
 * Handles scanner messages that are triggered by the Redux layer.
 * The handler keeps the UI-facing action names separate from the lower-level protocol work.
 */
export const TT_XRS2I_HANDLER = {
  [scannerMessages.CANCEL_READ_SAVED_SCANS]: ({
    deviceId,
  }: {
    deviceId: string;
  }) => truTestXRS2ICancelReadSavedScans(deviceId),
  [scannerMessages.READ_SAVED_SCANS]: ({ deviceId }: { deviceId: string }) =>
    truTestXRS2IReadSavedScans(deviceId),
};

/**
 * Driver-owned lifecycle hooks consumed by the generic device saga.
 */
export const TT_XRS2I_DRIVER_LIFECYCLE: DriverLifecycleHooks = {
  onConnected: truTestXRS2ISyncState,
  onDisconnected: truTestXRS2IDestroySession,
  onTick: truTestXRS2ITick,
  tickIntervalMs: 1000,
};

/**
 * Re-exports the concrete device factory used by the runtime session wrapper.
 * Callers use this to create a live protocol device with a transport and event callback.
 */
export { createXrs2iDevice } from "./device/createProtocol";
export type {
  DriverAction,
  DriverActionResult,
} from "./adapters/driverEffects";
export type {
  ImportedScan,
  IRxTransport,
  ITxTransport,
  Xrs2iEvent,
} from "./device/types";
